/**
 * Chuỗi VietQR (chuẩn NAPAS trên nền EMVCo Merchant-Presented QR) — hàm thuần, không gọi dịch vụ ngoài, không thêm
 * thư viện. Nguồn cấu trúc + mẫu kiểm: docs/vietqr.md.
 *
 * Hai nơi dùng: tiền cọc vào tài khoản của TIỆM (`apps/api/src/domain/tien/vietqr.ts` re-export file này, N2) và
 * mã chuyển khoản phí gói SAPA vào tài khoản của SAPA (`gia-sapa.ts`, kế hoạch 10/2026 mục 5.6). Nằm ở `packages/shared`
 * để web quầy dựng mã ngay trên máy, không đi vòng qua máy chủ.
 *
 * KHÔNG nằm trong barrel `index.ts` (có literal `0n`; Mini App build ES2015 không nhận) — nhập bằng
 * `@sapa/shared/vietqr`.
 *
 * Tiền đi qua dưới dạng `bigint` đồng; chỉ in ra chữ số (không dấu chấm, không thập phân).
 */

/** Một trường TLV: mã 2 số + độ dài 2 số + giá trị. Độ dài tính theo KÝ TỰ (giá trị chỉ ASCII). */
function tlv(ma: string, giaTri: string): string {
  if (giaTri.length > 99) throw new Error(`Truong ${ma} dai qua 99 ky tu`)
  return `${ma}${String(giaTri.length).padStart(2, '0')}${giaTri}`
}

/** CRC-16/CCITT-FALSE: đa thức 0x1021, khởi tạo 0xFFFF, không đảo bit, không XOR cuối. */
export function crc16CcittFalse(s: string): string {
  let crc = 0xffff
  for (let i = 0; i < s.length; i++) {
    crc ^= s.charCodeAt(i) << 8
    for (let b = 0; b < 8; b++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0')
}

/**
 * Nội dung chuyển khoản: bỏ dấu tiếng Việt, chỉ giữ chữ-số và khoảng trắng — nhiều app ngân hàng cắt /
 * từ chối ký tự đặc biệt. Tối đa 23 ký tự (vietqr.vn: 23; vietqr.io / EMVCo: 25 — lấy mức chặt hơn).
 */
export function lamSachNoiDung(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 23)
    .trim()
}

export function taoChuoiVietQr(input: {
  /** Mã BIN 6 số của ngân hàng nhận (vd Vietcombank 970436). */
  bin: string
  soTaiKhoan: string
  /**
   * Số tiền (đồng). Có → mã "động" (01=12) điền sẵn số tiền, dùng một lần. Bỏ trống → mã "tĩnh" (01=11): người trả tự
   * nhập số tiền — dùng cho phí gói SAPA, nơi SAPA chưa biết tiệm thuộc mức giá nào (ADR-094).
   */
  soTienVnd?: bigint
  noiDung: string
}): string {
  if (!/^[0-9]{6}$/.test(input.bin)) throw new Error('BIN ngan hang phai 6 chu so')
  if (!/^[0-9A-Za-z]{4,19}$/.test(input.soTaiKhoan)) throw new Error('So tai khoan khong hop le')
  const coTien = input.soTienVnd !== undefined
  if (input.soTienVnd !== undefined) {
    if (input.soTienVnd <= 0n || input.soTienVnd > 9_999_999_999_999n) {
      throw new Error('So tien coc khong hop le')
    }
  }
  const nguoiNhan = tlv('00', input.bin) + tlv('01', input.soTaiKhoan)
  const napas = tlv('00', 'A000000727') + tlv('01', nguoiNhan) + tlv('02', 'QRIBFTTA')
  const noiDung = lamSachNoiDung(input.noiDung)
  const than =
    tlv('00', '01') +
    // 12 = mã "động": có số tiền, dùng một lần cho đúng lịch này. 11 = mã "tĩnh": không có số tiền.
    tlv('01', coTien ? '12' : '11') +
    tlv('38', napas) +
    tlv('53', '704') +
    (input.soTienVnd === undefined ? '' : tlv('54', input.soTienVnd.toString())) +
    tlv('58', 'VN') +
    (noiDung === '' ? '' : tlv('62', tlv('08', noiDung))) +
    '6304'
  return than + crc16CcittFalse(than)
}
